import { MutationTuple, QueryResult } from '@apollo/client/react/types/types';
import { ApolloError, MutationUpdaterFn, useMutation, useQuery } from '@apollo/client';
import {
  ARCHIVE_INBOX,
  GENERATE_INBOX,
  GET_INBOX_EMAIL,
  GET_INBOX_EMAILS,
  GET_INBOX_TOTAL,
  GET_INBOXES,
  GET_BASE_INFO_OF_INBOXES,
  MARK_INBOX_EMAIL_AS_READ,
  MARK_INBOX_EMAIL_AS_UNREAD,
  UPDATE_INBOX,
  UPDATE_INBOX_EMAIL_STATUS,
  UPDATE_INBOX_NAME
} from './queries';
import { isString } from 'src/helpers';
import { useSearchParams } from 'react-router-dom';
import {
  EmailStatus,
  IInbox,
  IBaseInbox,
  IInboxEmail,
  IInboxEmailSearchVars,
  INextCallOff,
  IApiInbox,
  IApiBaseInbox
} from './types';
import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { updateQueryParams } from 'src/helpers/history';
import { CallOffsSubCategory, OverviewQp, ProcurementsDisplay } from '../procurements/types';
import { useCloseSidebar, useCurrentSidebar, useOpenSidebar } from 'src/shared/InfoSidebar/hooks';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import { SearchCallOffsContext, UpdateSearchCallOffsContext } from './context';
import { notification } from 'src/common';
import { useTranslation } from 'react-i18next';
import { AccessControlPolicy } from '../acl/types';
import { CallOffsData, BaseInbox, Inbox } from '.';
import { ICallOffData } from './types';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';

export function useCallOffsSearch(): IInboxEmailSearchVars {
  return useContext(SearchCallOffsContext);
}

export function useUpdateCallOffsSearch(): Dispatch<SetStateAction<IInboxEmailSearchVars>> {
  const ctx = useContext(UpdateSearchCallOffsContext);
  if (!ctx) {
    throw new Error('not in UpdateSearchCallOffsContext provider');
  }

  return ctx;
}

export function useLoadInboxes(
  skip?: boolean
): Omit<QueryResult<{ getInboxes: IApiInbox[] }>, 'data'> & { data?: IInbox[] } {
  const query = useQuery<{ getInboxes: IApiInbox[] }>(GET_INBOXES, { skip: !!skip });

  return useMemo(
    () => ({ ...query, data: query.data ? query.data.getInboxes.map(inbox => new Inbox(inbox)) : undefined }),
    [query]
  );
}
export function useLoadInboxesNav(
  skip?: boolean
): Omit<QueryResult<{ getInboxes: IApiBaseInbox[] }>, 'data'> & { data?: IBaseInbox[] } {
  const query = useQuery<{ getInboxes: IApiBaseInbox[] }>(GET_BASE_INFO_OF_INBOXES, { skip: !!skip });

  return useMemo(
    () => ({ ...query, data: query.data ? query.data.getInboxes.map(inbox => new BaseInbox(inbox)) : undefined }),
    [query]
  );
}

interface IGenerateInboxResponse {
  __typename: 'Mutation';
  generateInbox: IInbox & {
    __typename: 'CompanyInbox';
  };
}

export function useGenerateInbox(): MutationTuple<IGenerateInboxResponse, { inboxName: string }> {
  return useMutation(GENERATE_INBOX);
}

export function getUpdateCacheOnGenerateInbox(): MutationUpdaterFn<IGenerateInboxResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const inboxesQueryData = cache.readQuery<{ getInboxes: IApiInbox[] } | null>({
      query: GET_INBOXES
    });
    if (inboxesQueryData) {
      const { getInboxes } = inboxesQueryData;
      cache.writeQuery({
        query: GET_INBOXES,
        data: { getInboxes: [...getInboxes, data.generateInbox] }
      });
    }
  };
}

export interface ILoadInboxEmailsResponse {
  getInboxEmails: {
    __typename: 'MailResponse';
    total: number;
    userMails: IInboxEmail[];
  };
}

export interface ILoadInboxEmailsRequest {
  from?: number;
  size?: number;
  keywords?: string[];
  statuses?: EmailStatus[];
  emails?: string[] | null;
}

export function useLoadInboxTotal(): QueryResult<ILoadInboxEmailsResponse, ILoadInboxEmailsRequest> {
  const [inbox] = useActiveInboxState();
  return useQuery<ILoadInboxEmailsResponse, ILoadInboxEmailsRequest>(GET_INBOX_TOTAL, {
    variables: { emails: [(!!inbox && inbox.emailAddress) || ''] },
    skip: !inbox
  });
}

export function useLoadInboxEmails(
  vars: IInboxEmailSearchVars
): Omit<QueryResult<ILoadInboxEmailsResponse, ILoadInboxEmailsRequest>, 'data'> & { data?: ICallOffData } {
  const { keywords, from, size, statuses } = vars;
  const [inbox] = useActiveInboxState();
  const query = useQuery<ILoadInboxEmailsResponse, ILoadInboxEmailsRequest>(GET_INBOX_EMAILS, {
    variables: {
      keywords,
      from,
      size,
      statuses,
      emails: [(!!inbox && inbox.emailAddress) || '']
    },
    skip: !inbox
  });
  return useMemo(
    () => ({
      ...query,
      data: (query.data && new CallOffsData(query.data)) || undefined
    }),
    [query]
  );
}

export function useLoadInboxEmail(
  id: string | null,
  skip?: boolean
): QueryResult<{ getInboxEmail: { userMails: IInboxEmail[] } }> {
  return useQuery<{ getInboxEmail: { userMails: IInboxEmail[] } }>(GET_INBOX_EMAIL, {
    variables: { ids: [id] },
    skip: !id && !!skip
  });
}

interface IUpdateInbox {
  __typename: 'Mutation';
  updateInbox: IInbox & {
    __typename: 'CompanyInbox';
  };
}

export function useUpdateInboxName(): MutationTuple<IUpdateInbox, { inboxName: string; inboxId: string }> {
  return useMutation(UPDATE_INBOX_NAME);
}

export function useActiveInboxState(): [IInbox | undefined, (id: string) => void] {
  const [searchParams] = useSearchParams();
  const isCallOffFeature = useFeatureFlag(FeatureFlag.Mercury);
  const { data: inboxes, loading } = useLoadInboxes(!isCallOffFeature);
  const qpInboxMail = searchParams.get(OverviewQp.Inbox);
  const activeInbox = useMemo(() => {
    if (!inboxes || loading) {
      return;
    }
    return (
      (isString(qpInboxMail) && inboxes.find(_ => _.emailAddress === qpInboxMail)) ||
      (!!inboxes.length && inboxes[0]) ||
      undefined
    );
  }, [inboxes, loading, qpInboxMail]);
  const setActiveInbox = useCallback((id: string) => {
    updateQueryParams({
      [OverviewQp.Inbox]: id,
      [OverviewQp.Tab]: ProcurementsDisplay.CALL_OFFS,
      [OverviewQp.SubCategory]: CallOffsSubCategory.NEW
    });
  }, []);

  return [activeInbox, setActiveInbox];
}

export function useBaseInfoOfActiveInbox(): IBaseInbox | undefined {
  const [searchParams] = useSearchParams();
  const isCallOffFeature = useFeatureFlag(FeatureFlag.Mercury);
  const { data: inboxes, loading } = useLoadInboxesNav(!isCallOffFeature);
  const qpInboxMail = searchParams.get(OverviewQp.Inbox);
  return useMemo(() => {
    if (!inboxes || loading) {
      return;
    }
    return (
      (isString(qpInboxMail) && inboxes.find(_ => _.emailAddress === qpInboxMail)) ||
      (!!inboxes.length && inboxes[0]) ||
      undefined
    );
  }, [inboxes, loading, qpInboxMail]);
}

export function useActiveInboxEmail(): { data?: IInboxEmail; loading: boolean; error?: ApolloError } {
  const sidebarState = useCurrentSidebar();
  const {
    data: inboxEmailData,
    loading,
    error
  } = useLoadInboxEmail(sidebarState && sidebarState.id, sidebarState?.mode === SidebarMode.CALL_OFF_INFO);
  return useMemo(
    () => ({
      data:
        inboxEmailData && inboxEmailData.getInboxEmail.userMails
          ? inboxEmailData.getInboxEmail.userMails[0]
          : undefined,
      loading,
      error
    }),
    [error, inboxEmailData, loading]
  );
}
interface IUpdateInboxEmailStatusResponse {
  __typename: 'Mutation';
  setInboxMailsStatus: IInboxEmail[] & {
    __typename: 'UserMail';
  };
}

export function useUpdateInboxEmailStatus(): [
  (mailIds: string[], status: EmailStatus) => void,
  { loading: boolean; error: Error | undefined }
] {
  const [updateInboxEmailsStatus, { loading, error }] = useMutation<
    IUpdateInboxEmailStatusResponse,
    { mailIds: string[]; status: EmailStatus }
  >(UPDATE_INBOX_EMAIL_STATUS);
  const [inbox] = useActiveInboxState();
  const { t } = useTranslation();

  const updateInboxEmailsStatusFn = useCallback(
    (mailIds: string[], status: EmailStatus) => {
      if (inbox) {
        updateInboxEmailsStatus({
          variables: { mailIds, status }
        }).catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
      }
    },
    [inbox, t, updateInboxEmailsStatus]
  );
  return [updateInboxEmailsStatusFn, { loading, error }];
}

interface IMarkInboxEmailAsRead {
  __typename: 'Mutation';
  setInboxMailsRead: IInboxEmail & {
    __typename: 'UserMail';
  };
}

export function useMarkInboxEmailsAsRead(): MutationTuple<IMarkInboxEmailAsRead, { mailIds: string[] }> {
  return useMutation(MARK_INBOX_EMAIL_AS_READ);
}
export function useMarkInboxEmailsAsUnread(): MutationTuple<IMarkInboxEmailAsRead, { mailIds: string[] }> {
  return useMutation(MARK_INBOX_EMAIL_AS_UNREAD);
}

interface IArchiveInboxResult {
  __typename: 'Mutation';
  updateArchived: IInbox & {
    __typename: 'CompanyInbox';
  };
}

export function useArchiveInbox(): MutationTuple<IArchiveInboxResult, { id: string }> {
  return useMutation<IArchiveInboxResult, { id: string }>(ARCHIVE_INBOX);
}

export function useUpdateInbox(): MutationTuple<
  IUpdateInbox,
  {
    inboxId: string;
    subscribers?: string[];
    owners?: string[];
    subscribePolicy?: AccessControlPolicy;
    editPolicy?: AccessControlPolicy;
  }
> {
  return useMutation(UPDATE_INBOX);
}

export function useOpenCallOffPreview(): (callOff?: INextCallOff) => void {
  const onCloseSidebar = useCloseSidebar();
  const onOpenSideBar = useOpenSidebar();
  const [markInboxEmailsAsRead] = useMarkInboxEmailsAsRead();

  return useCallback(
    (callOff?: INextCallOff) => {
      if (!!callOff) {
        const { id, isRead } = callOff;
        onOpenSideBar({
          id: id,
          mode: SidebarMode.CALL_OFF_INFO
        });
        !isRead && markInboxEmailsAsRead({ variables: { mailIds: [id] } }).catch(err => console.error(err));
      } else {
        onCloseSidebar();
      }
    },
    [markInboxEmailsAsRead, onCloseSidebar, onOpenSideBar]
  );
}
